export const buildMigrationMessage = ({ holder_address, eth_address, timestamp }) => {
    return [
        'Flash SOL to ETH migration',
        `Solana: ${holder_address}`,
        `Ethereum: ${String(eth_address).trim().toLowerCase()}`,
        `Timestamp: ${timestamp}`
    ].join('\n');
};

const BASE58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

const decodeBase58 = (value) => {
    const bytes = [0];
    for (const char of value) {
        const index = BASE58.indexOf(char);
        if (index < 0) {
            throw new Error('Invalid base58 signature');
        }
        let carry = index;
        for (let i = 0; i < bytes.length; i += 1) {
            carry += bytes[i] * 58;
            bytes[i] = carry & 0xff;
            carry >>= 8;
        }
        while (carry > 0) {
            bytes.push(carry & 0xff);
            carry >>= 8;
        }
    }
    for (const char of value) {
        if (char !== '1') {
            break;
        }
        bytes.push(0);
    }
    return Uint8Array.from(bytes.reverse());
};

export const toSignatureBytes = (signed) => {
    if (!signed) {
        throw new Error('Wallet returned an empty signature');
    }
    if (signed instanceof Uint8Array) {
        return signed;
    }
    if (ArrayBuffer.isView(signed)) {
        return new Uint8Array(signed.buffer, signed.byteOffset, signed.byteLength);
    }
    if (typeof ArrayBuffer !== 'undefined' && signed instanceof ArrayBuffer) {
        return new Uint8Array(signed);
    }
    if (Array.isArray(signed)) {
        return Uint8Array.from(signed);
    }
    if (signed.signature) {
        return toSignatureBytes(signed.signature);
    }
    if (typeof signed === 'string') {
        const compact = signed.trim();
        try {
            const base64 = Uint8Array.from(atob(compact), (char) => char.charCodeAt(0));
            if (base64.length === 64) {
                return base64;
            }
        } catch (error) {
            // Fall through to base58.
        }
        const base58 = decodeBase58(compact);
        if (base58.length === 64) {
            return base58;
        }
    }
    throw new Error('Wallet returned an unsupported signature format');
};

export const bytesToBase64 = (bytes) => {
    const signature = toSignatureBytes(bytes);
    let binary = '';
    signature.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary);
};

export const isUserRejectedSignature = (error) => {
    const text = `${error?.name || ''} ${error?.message || ''}`.toLowerCase();
    return /reject|denied|cancel/.test(text);
};

export const canFallbackToMemo = (error) => {
    if (isUserRejectedSignature(error)) {
        return false;
    }
    const text = `${error?.name || ''} ${error?.message || ''}`.toLowerCase();
    return /sign ?messages?|not support|ledger|hardware|walletaccounterror|walletconfigerror|feature|empty signature|unsupported signature/.test(text)
        || error?.name === 'WalletAccountError'
        || error?.name === 'WalletConfigError'
        || error?.name === 'WalletSignMessageError';
};

export const getSubmitErrorMessage = (error) => {
    const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
    if (apiMessage) {
        return apiMessage;
    }
    if (isUserRejectedSignature(error)) {
        return 'Signature cancelled. Please approve the request in your Solana wallet.';
    }
    const text = `${error?.message || ''}`.toLowerCase();
    if (/blockhash|403|access forbidden/.test(text)) {
        return 'Ledger could not fetch a Solana blockhash. Refresh and submit again — the site now proxies this request.';
    }
    if (error?.message) {
        return error.message;
    }
    return 'Submission failed. Please try again from the Phantom app browser.';
};
